import axios from "axios";

const BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

// Global token getter - set by AuthContext when user authenticates
let globalGetToken: (() => Promise<string | null>) | null = null;

/**
 * Set the global token getter function.
 * Called by AuthContext to enable authentication on the default api client.
 */
export function setGlobalTokenGetter(getToken: () => Promise<string | null>) {
  globalGetToken = getToken;
}

/**
 * Get the current auth token using the global getter.
 * Returns null if no getter is set or token retrieval fails.
 */
export async function getAuthToken(): Promise<string | null> {
  if (!globalGetToken) {
    return null;
  }
  try {
    return await globalGetToken();
  } catch (error) {
    console.error("Failed to get auth token:", error);
    return null;
  }
}

// Default API client - now with authentication
export const api = axios.create({
  baseURL: BASE_URL,
});

// Add auth token to all requests
api.interceptors.request.use(async (config) => {
  const token = await getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors - let Clerk handle redirects via SignedOut component
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on 401 - Clerk's SignedOut component handles auth redirects
    return Promise.reject(error);
  }
);

/**
 * Create an API client with explicit token getter.
 * Use this when you need a separate client instance or in non-React contexts.
 */
export function createApiClient(getToken: () => Promise<string | null>) {
  const client = axios.create({
    baseURL: BASE_URL,
  });

  // Add auth token to requests
  client.interceptors.request.use(async (config) => {
    try {
      const token = await getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error("Failed to get auth token:", error);
    }
    return config;
  });

  // Handle auth errors - let Clerk handle redirects via SignedOut component
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      // Don't redirect on 401 - Clerk's SignedOut component handles auth redirects
      return Promise.reject(error);
    }
  );

  return client;
}
